/**
 * Global hook that mirrors Tailwind CSS breakpoints in JavaScript.
 * Listens to screen size changes via `matchMedia` and exposes:
 *   - `useBreakpoints()` → returns an object with the active breakpoints.
 *   - `useBreakpoint()` → returns the highest active breakpoint.
 *   - `useBreakpointValue(values)` → returns a value based on the current breakpoint (similar to Chakra UI).
*/

// React 17 -> shim 
// React 18 -> import { useSyncExternalStore } from 'react'
import { useSyncExternalStore } from 'use-sync-external-store/shim'

// tailwind.config.js
const QUERIES = {
  xs: '(min-width: 420px)',
  sm: '(min-width: 640px)',
  md: '(min-width: 768px)',
  lg: '(min-width: 1024px)',
  xl: '(min-width: 1280px)',
  '2xl': '(min-width: 1536px)',
  '8/3xl': '(min-width: 1800px)',
  '3xl': '(min-width: 1920px)',
  '4xl': '(min-width: 2560px)',
} as const

type Keys = keyof typeof QUERIES
type Snapshot = Record<Keys, boolean>

const mqs: Partial<Record<Keys, MediaQueryList>> = {}
const listeners = new Map<Keys, (e: MediaQueryListEvent) => void>()

// Global
let snapshot: Snapshot = {
  xs: false, sm: false, md: false, lg: false, xl: false,
  '2xl': false, '8/3xl': false, '3xl': false, '4xl': false,
}

let initialized = false
const subs = new Set<() => void>()

function computeSnapshot(): Snapshot {
  const next = { ...snapshot };

  (Object.keys(QUERIES) as Keys[]).forEach((k) => {
    mqs[k] = mqs[k] ?? window.matchMedia(QUERIES[k])
    next[k] = !!mqs[k]?.matches
  })

  return next
}

function ensureInit() {
  if (initialized || typeof window === 'undefined') return
  
  snapshot = computeSnapshot();
  
  (Object.keys(QUERIES) as Keys[]).forEach((k) => {
    const mq = mqs[k]!
    const onChange = () => {
      const next = computeSnapshot()
      let changed = false
      
      for (const kk of Object.keys(next) as Keys[]) {
        if (next[kk] !== snapshot[kk]) { changed = true; break }
      }
      
      if (changed) {
        snapshot = next
        subs.forEach((cb) => cb())
      }
    }
    listeners.set(k, onChange)
    mq.addEventListener('change', onChange)
  })

  initialized = true
}

function subscribe(cb: () => void) {
  ensureInit()
  subs.add(cb)
  
  return () => { subs.delete(cb) }
}

function getSnapshot(): Snapshot { return snapshot }
function getServerSnapshot(): Snapshot { return snapshot }

// Returns an object with booleans for each breakpoint
export function useBreakpoints(): Snapshot {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot)
}

// Returns the name of the highest active breakpoint
export function useBreakpoint(): Keys | 'base' {
  const bp = useBreakpoints()
  const order: Array<Keys> = ['xs','sm','md','lg','xl','2xl','8/3xl','3xl','4xl']
  
  for (let i = order.length - 1; i >= 0; i--) {
    const k = order[i]
    if (bp[k]) return k
  }

  return 'base'
}

// Selects a value based on the current breakpoint (fallbacks to smaller ones)
export function useBreakpointValue<T>(values: Partial<Record<'base' | Keys, T>>) {
  const current = useBreakpoint()
  const order: Array<'base' | Keys> = ['base','xs','sm','md','lg','xl','2xl','8/3xl','3xl','4xl']
  const idx = order.indexOf(current)
  
  for (let i = idx; i >= 0; i--) {
    const k = order[i]
    if (values[k] !== undefined) return values[k] as T
  }
  
  return undefined
}
